import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Download, RefreshCw, Upload } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { listEmpresasPorPapel } from '../../services/pilotoApi';
import {
  downloadErrosLoteMtr,
  importarLoteMtr,
  listErrosLoteMtr,
  listLotesMtr,
} from '../../services/mtrImportacaoApi';
import { EmpresaPiloto } from '../../types/piloto';
import { MtrErroImportacao, MtrLoteImportacao } from '../../types/mtrImportacao';

const exemploCsv = [
  'numeroMtr,identificadorExterno,codigoLeitoInformado,identificadorInternoInformado,geradorId,transportadorId,destinadorId,dataEmissao,descricao,grupoRss,quantidade,unidadeMedida,classeResiduo,estadoFisico,acondicionamento,tratamentoPrevisto',
  'MTR-001,EXT-001,LEITO-001,,gerador-id,transportador-id,destinador-id,2026-06-10,Residuo infectante,A,2,kg,,,saco branco,autoclave',
].join('\n');

const ImportacaoMTR: React.FC = () => {
  const { accessToken } = useAuth();
  const [empresas, setEmpresas] = useState<EmpresaPiloto[]>([]);
  const [empresaOrigemId, setEmpresaOrigemId] = useState('');
  const [sistemaOrigem, setSistemaOrigem] = useState('sistema_externo');
  const [formato, setFormato] = useState<'json' | 'csv'>('csv');
  const [conteudo, setConteudo] = useState(exemploCsv);
  const [lotes, setLotes] = useState<MtrLoteImportacao[]>([]);
  const [erros, setErros] = useState<MtrErroImportacao[]>([]);
  const [selectedLoteId, setSelectedLoteId] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!accessToken) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const [empresasResult, lotesResult] = await Promise.all([
        listEmpresasPorPapel(accessToken, 'gerador'),
        listLotesMtr(accessToken),
      ]);
      setEmpresas(empresasResult.items);
      setEmpresaOrigemId((current) => current || empresasResult.items[0]?.id || '');
      setLotes(lotesResult.items);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Erro ao carregar importação.');
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    load();
  }, [load]);

  const handleFile = async (file?: File) => {
    if (!file) {
      return;
    }

    setConteudo(await file.text());
    setFormato(file.name.toLowerCase().endsWith('.json') ? 'json' : 'csv');
  };

  const handleImportar = async () => {
    if (!accessToken || !empresaOrigemId) {
      setError('Selecione a empresa origem do lote.');
      return;
    }

    setLoading(true);
    setError(null);
    setMessage(null);

    try {
      const lote = await importarLoteMtr(accessToken, {
        empresaOrigemId,
        sistemaOrigem,
        formato,
        conteudo,
      });
      setMessage(`Lote ${lote.id} processado com status ${lote.status}.`);
      setSelectedLoteId(lote.id);
      const loteErros = await listErrosLoteMtr(accessToken, lote.id);
      setErros(loteErros);
      await load();
    } catch (importError) {
      setError(importError instanceof Error ? importError.message : 'Erro ao importar lote.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelecionarLote = async (loteId: string) => {
    if (!accessToken) {
      return;
    }

    setSelectedLoteId(loteId);
    setErros(await listErrosLoteMtr(accessToken, loteId));
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 border-b border-gray-200 pb-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">Importação de MTR</h2>
          <p className="mt-1 text-sm text-gray-600">
            Importe CSV ou JSON de sistemas externos para o modelo canônico de MTR.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
        >
          <RefreshCw size={16} />
          Atualizar
        </button>
      </div>

      {message ? <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">{message}</div> : null}
      {error ? (
        <div className="flex gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900">
          <AlertTriangle size={18} />
          <span>{error}</span>
        </div>
      ) : null}

      <section className="grid gap-4 rounded-md border border-gray-200 bg-white p-4 lg:grid-cols-3">
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500">Empresa origem</label>
          <select value={empresaOrigemId} onChange={(event) => setEmpresaOrigemId(event.target.value)} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm">
            {empresas.map((empresa) => <option key={empresa.id} value={empresa.id}>{empresa.razaoSocial}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500">Sistema origem</label>
          <input value={sistemaOrigem} onChange={(event) => setSistemaOrigem(event.target.value)} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500">Formato</label>
          <select value={formato} onChange={(event) => setFormato(event.target.value as 'json' | 'csv')} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm">
            <option value="csv">CSV</option>
            <option value="json">JSON</option>
          </select>
        </div>
        <div className="lg:col-span-3">
          <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500">Arquivo</label>
          <input type="file" accept=".csv,.json,text/csv,application/json" onChange={(event) => handleFile(event.target.files?.[0])} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm" />
        </div>
        <div className="lg:col-span-3">
          <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500">Conteúdo</label>
          <textarea value={conteudo} onChange={(event) => setConteudo(event.target.value)} rows={10} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-xs" />
        </div>
        <div>
          <button type="button" disabled={loading} onClick={handleImportar} className="inline-flex items-center gap-2 rounded-md bg-emerald-700 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-800 disabled:bg-gray-300">
            <Upload size={16} />
            Importar lote
          </button>
        </div>
      </section>

      <section className="grid gap-4 xl:grid-cols-2">
        <div className="rounded-md border border-gray-200 bg-white">
          <div className="border-b border-gray-200 px-4 py-3 text-sm font-semibold text-gray-700">Lotes</div>
          <div className="divide-y divide-gray-100">
            {lotes.map((lote) => (
              <button key={lote.id} type="button" onClick={() => handleSelecionarLote(lote.id)} className={`block w-full px-4 py-3 text-left text-sm hover:bg-gray-50 ${selectedLoteId === lote.id ? 'bg-emerald-50' : ''}`}>
                <div className="font-medium text-gray-900">{lote.status}</div>
                <div className="text-gray-600">Recebidos {lote.quantidadeRecebida} · Processados {lote.quantidadeProcessada} · Erros {lote.quantidadeErros}</div>
                <div className="text-xs text-gray-500">{lote.id}</div>
              </button>
            ))}
          </div>
        </div>
        <div className="rounded-md border border-gray-200 bg-white">
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
            <div className="text-sm font-semibold text-gray-700">Erros do lote</div>
            <button type="button" disabled={!selectedLoteId} onClick={() => selectedLoteId && accessToken && downloadErrosLoteMtr(accessToken, selectedLoteId)} className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              <Download size={14} />
              CSV
            </button>
          </div>
          <div className="divide-y divide-gray-100">
            {erros.length === 0 ? <div className="p-4 text-sm text-gray-500">Nenhum erro selecionado.</div> : null}
            {erros.map((erro) => (
              <div key={erro.id} className="p-4 text-sm">
                <div className="font-medium text-gray-900">Linha {erro.linha} · {erro.codigo}</div>
                <div className="mt-1 text-gray-600">{erro.mensagem}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ImportacaoMTR;
